/**
 * Shared PICO Colors Constants
 * Used across Screening Space components for consistent color coding
 * 
 * Usage:
 * import { PICO_COLORS, normalizePicoKey, normalizePicoJson } from '../../../../utils/picoColors';
 */

export const PICO_COLORS = {
    population: "#3b4eff",
    intervention: "#4aa3df",
    comparison: "#d65a1f",
    outcome: "#7a1e6c",
    study_design: "#2E8B57",
    exclusion: "#DC3545",
};

/**
 * Normalize a PICO key to match PICO_COLORS keys
 * Handles variations like "Population" -> "population", "comparator" -> "comparison"
 * 
 * @param {string} key - The PICO key to normalize
 * @returns {string} - Normalized key
 */
export const normalizePicoKey = (key) => {
    if (!key) return "";
    
    let normalized = String(key).trim().toLowerCase();
    
    // Handle common variations
    if (normalized === "comparator" || normalized === "comparators") {
        normalized = "comparison";
    }
    if (normalized === "study design") {
        normalized = "study_design";
    }
    if (normalized === "exclude") {
        normalized = "exclusion";
    }
    
    return normalized;
};

/**
 * Normalize entire PICO JSON object
 * Converts all keys to lowercase and handles variations
 * 
 * @param {Object} raw - Raw PICO JSON from API
 * @returns {Object} - Normalized PICO JSON
 */
export const normalizePicoJson = (raw) => {
    if (!raw || typeof raw !== "object") return {};
    
    const out = {};
    
    Object.entries(raw).forEach(([key, value]) => {
        const normalizedKey = normalizePicoKey(key);
        
        if (Array.isArray(value)) {
            out[normalizedKey] = value
                .map((v) => (v == null ? "" : String(v).trim()))
                .filter(Boolean);
        } else if (value && typeof value === "object" && !Array.isArray(value)) {
            // New nested format: {CATEGORY: {subcategory: [tags]}}
            const allTags = [];
            Object.values(value).forEach((arr) => {
                if (Array.isArray(arr)) {
                    arr.forEach((v) => {
                        const t = (v == null ? "" : String(v).trim());
                        if (t) allTags.push(t);
                    });
                }
            });
            out[normalizedKey] = allTags;
        }
    });
    
    return out;
};

/**
 * Get PICO type for a given tag/keyword
 * 
 * @param {string} tag - The tag to check
 * @param {Object} picoJson - The PICO JSON object
 * @returns {string|null} - The PICO type or null if not found
 */
export const getPicoTypeForTag = (tag, picoJson = {}) => {
    if (!tag) return null;
    
    const lowerTag = tag.toLowerCase();
    const normalized = normalizePicoJson(picoJson);
    
    for (const [type, keywords] of Object.entries(normalized)) {
        if (!Array.isArray(keywords)) continue;
        
        const match = keywords.find((k) =>
            lowerTag.includes(String(k).toLowerCase())
        );
        
        if (match) return type;
    }
    
    return null;
};

/**
 * Highlight PICO keywords in text with colored spans
 * 
 * @param {string} text - The text to highlight
 * @param {Object} picoJson - The PICO JSON object
 * @returns {string} - HTML string with highlighted keywords
 */
export const highlightPICO = (text, picoJson = {}) => {
    if (!text) return "";
    
    // Normalize first so both flat and nested formats work
    const normalized = normalizePicoJson(picoJson);
    let highlighted = text;
    
    Object.entries(normalized).forEach(([type, keywords]) => {
        if (!PICO_COLORS[type]) return;
        if (!Array.isArray(keywords)) return;
        
        // Sort keywords by length (longest first) to avoid partial replacement issues
        const sortedKeywords = [...keywords].sort((a, b) => b.length - a.length);
        
        sortedKeywords.forEach((word) => {
            if (!word) return;
            
            // Escape regex special chars
            const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
            
            const regex = new RegExp(`(${escaped})`, "gi");
            highlighted = highlighted.replace(
                regex,
                `<span style="color: ${PICO_COLORS[type]}; font-weight: 600;">$1</span>`
            );
        });
    });
    
    return highlighted;
};

/**
 * Build TagFilterPanel-compatible tag options from pico_json
 * Handles both flat and nested formats
 *
 * @param {Object} picoJson - The PICO JSON from API
 * @returns {Array} - Array of { id, tag, picos_category, subcategory }
 */
export const buildTagOptionsFromPicoJson = (picoJson) => {
    if (!picoJson || typeof picoJson !== "object") return [];
    const tags = [];

    Object.entries(picoJson).forEach(([rawKey, value]) => {
        const category = normalizePicoKey(rawKey);

        if (Array.isArray(value)) {
            // Legacy flat format: {CATEGORY: [tags]}
            value.forEach((val) => {
                const text = (val == null ? "" : String(val)).trim();
                if (!text) return;
                tags.push({ id: text, tag: text, picos_category: category });
            });
        } else if (value && typeof value === "object") {
            // New nested format: {CATEGORY: {subcategory: [tags]}}
            Object.entries(value).forEach(([subcategory, arr]) => {
                if (!Array.isArray(arr)) return;
                arr.forEach((val) => {
                    const text = (val == null ? "" : String(val)).trim();
                    if (!text) return;
                    tags.push({ id: text, tag: text, picos_category: category, subcategory });
                });
            });
        }
    });

    const seen = new Set();
    const unique = [];
    for (const t of tags) {
        if (seen.has(t.id)) continue;
        seen.add(t.id);
        unique.push(t);
    }
    return unique;
};

export default PICO_COLORS;